import { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import gql from 'graphql-tag'
import { yupResolver } from '@hookform/resolvers'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'
import { gtmEvent } from '@lib/gtm-utils'
import { useTrackCoachProfileEvent } from '@lib/pga-coach/coach-profile-analytics'
import { isRecaptchaRequired } from '@lib/isRecaptchaRequired'

export const LEAD_SOURCE = 'PGACOM_PROFILE_DIRECT'

const phoneRegExp = /^\D?(\d{3})\D?\D?(\d{3})\D?(\d{4})$/

export const leadValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .trim()
    .max(50)
    .required('First name is required')
    .label('First name'),
  lastName: Yup.string()
    .trim()
    .max(100)
    .required('Last name is required')
    .label('Last name'),
  email: Yup.string()
    .email('Please enter a valid email')
    .max(100)
    .required('Email is required')
    .label('Email'),
  phoneNumber: Yup.string()
    .required('Phone number is required')
    .matches(phoneRegExp, 'Please enter a valid phone number')
    .label('Phone number'),
  interest: Yup.string().required('You must select an interest'),
})

const COACHING_LEAD_CREATE = gql`
  mutation coachingLeadCreate($params: CoachingLeadCreateRequest!) {
    CoachingLeadCreate(params: $params) {
      success
      message
    }
  }
`

export const getLeadCoachName = (coach) => (
  (coach.firstName && coach.lastName) ? `${coach.firstName} ${coach.lastName}` : coach.displayName
)

/**
 * Form state, reCAPTCHA gating, the Coach Tools `CoachingLeadCreate`
 * mutation, and completion analytics shared by every coach-profile lead
 * form layout. `position` tags the Amplitude completion event so the
 * sidebar and in-page forms can be compared. `marketingOptInVariant` must
 * match the `MarketingOptIn` treatment the layout renders.
 * `alreadySubmitted` shows the confirmation when another host on the page
 * already sent this visitor's lead; `submittedHere` stays false so only the
 * form that submitted moves focus or scroll.
 */
export default function useCoachingLeadForm({
  coach,
  position,
  marketingOptInVariant = 'callout',
  deferRecaptchaUntilVisible = false,
  recaptchaHostVisible = true,
  alreadySubmitted = false,
  onSubmitted,
}) {
  const trackCoachProfileEvent = useTrackCoachProfileEvent()
  const [submittedHere, setSubmitted] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState(null)
  const [marketingOptInChecked, setMarketingOptInChecked] = useState(false)
  const coachName = getLeadCoachName(coach)

  const recaptchaRequired = isRecaptchaRequired()
  const shouldMountRecaptcha = recaptchaRequired && (!deferRecaptchaUntilVisible || recaptchaHostVisible)

  useEffect(() => {
    if (recaptchaRequired && deferRecaptchaUntilVisible && !recaptchaHostVisible) {
      setRecaptchaToken(null)
    }
  }, [recaptchaRequired, deferRecaptchaUntilVisible, recaptchaHostVisible])

  const form = useForm({
    resolver: yupResolver(leadValidationSchema),
  })
  const { getValues, setError } = form

  const [coachingLeadCreate, { error: submitError }] = useMutation(
    COACHING_LEAD_CREATE,
    {
      context: { clientName: 'coach-tools', recaptchaToken },
      onCompleted: (data) => {
        const leadEmail = getValues('email')
        const leadIntent = getValues('interest')

        if (data.CoachingLeadCreate.success) {
          setSubmitted(true)
          onSubmitted?.()
          gtmEvent({
            event: 'formSubmit',
            attributes: {
              formSource: LEAD_SOURCE,
              formCategory: 'lead-submitted',
              formAction: 'lead-form-submit',
            },
          })
          gtmEvent({
            event: 'complete-lead-submission',
            attributes: {
              coach_name: coachName,
              coach_id: coach.coach?.id,
              facility: coach.facilities?.length > 0 ? coach.facilities[0].facilityName : null,
              city: coach.city,
              state: coach.state,
              lead_email: leadEmail,
              lead_intent: leadIntent,
              source: LEAD_SOURCE,
            },
          })
          trackCoachProfileEvent('complete-coach-profile-lead-submission', {
            lead_intent: leadIntent,
            marketing_opt_in: marketingOptInChecked,
            source: LEAD_SOURCE,
            position,
          })
          if (marketingOptInChecked) {
            gtmEvent({
              event: 'marketing-opt-in',
              attributes: {
                source: 'embedded_lead_form',
                variant: marketingOptInVariant,
              },
            })
          }
        } else {
          setError('submit', {
            type: 'manual',
            message: data.CoachingLeadCreate.message,
          })
        }
      },
    },
  )

  const onSubmit = async (data) => {
    const marketingOptInAt = marketingOptInChecked
      ? new Date().toISOString()
      : null

    try {
      await coachingLeadCreate({
        variables: {
          params: {
            coachSlug: coach.customSlug,
            email: data.email,
            phoneNumber: `+1${data.phoneNumber.replace(/\D/g, '')}`,
            firstName: data.firstName,
            lastName: data.lastName,
            intent: data.interest,
            source: LEAD_SOURCE,
            marketingOptInAt,
          },
        },
      })
    } catch (e) {
      // Apollo exposes the error via submitError; avoid unhandled promise rejections.
    }
  }

  const submitErrorMessage = (submitError || form.errors.submit)
    ? (form.errors.submit?.message || 'An error has occurred. Please try again later.')
    : null

  return {
    form,
    coachName,
    submitted: submittedHere || alreadySubmitted,
    submittedHere,
    onSubmit,
    submitErrorMessage,
    recaptchaRequired,
    shouldMountRecaptcha,
    recaptchaToken,
    setRecaptchaToken,
    marketingOptInChecked,
    setMarketingOptInChecked,
    submitDisabled: form.formState.isSubmitting || (recaptchaRequired && !recaptchaToken),
  }
}
